import { reducedStream, reducer, streamReducer } from '@ardoq/rxbeach';
import { resetState } from '@ardoq/common-helpers';
import type { AdHocSearchReport } from '@ardoq/report-builder';
import type { ReportCopySeed } from 'router/navigationActions';
import {
  navigateToReportBuilder,
  NavigateToReportBuilderPayload,
  NavigateToReportModulePayload,
  navigateToReportReader,
} from 'router/navigationActions';
import { ReportModule } from '../types';
import { reportNavigationReset } from './actions';
import { reportRouteNavigation$ } from './reportRouteNavigation$';
import {
  ReportModuleRoute,
  ReportNavigationState,
  ReportNavigationTransientState,
} from './types';

export type { ReportNavigationState };

const defaultState: ReportNavigationState = {
  reportModule: ReportModule.OVERVIEW,
};

const emptyBuilderSeed: ReportNavigationTransientState = {
  datasource: undefined,
  query: undefined,
  workspaceIds: undefined,
  columns: undefined,
  reportCopy: undefined,
  folderId: undefined,
};

const emptyReaderContext: ReportNavigationTransientState = {
  dashboardId: undefined,
  dashboardFilterValues: undefined,
  readerContextReportId: undefined,
};

const keepsReaderContext = (
  state: ReportNavigationState,
  route: ReportModuleRoute
): boolean =>
  route.reportModule === ReportModule.READER &&
  route.reportId === state.readerContextReportId;

const applyRouteFields = (
  state: ReportNavigationState,
  route: ReportModuleRoute
): ReportNavigationState => ({
  ...state,
  ...(route.reportModule === ReportModule.BUILDER ? {} : emptyBuilderSeed),
  ...(keepsReaderContext(state, route) ? {} : emptyReaderContext),
  ...route,
});

const isAdHocSearchReport = (
  payload: NavigateToReportBuilderPayload
): payload is AdHocSearchReport => 'datasource' in payload;

const isReportCopySeed = (
  payload: NavigateToReportBuilderPayload
): payload is ReportCopySeed => 'copyOfReportId' in payload;

const toBuilderSeed = (
  payload: NavigateToReportBuilderPayload
): ReportNavigationTransientState => {
  if (isReportCopySeed(payload)) {
    return { ...emptyBuilderSeed, reportCopy: payload };
  }
  if (!isAdHocSearchReport(payload)) {
    return { ...emptyBuilderSeed, folderId: payload.folderId };
  }
  return {
    ...emptyBuilderSeed,
    datasource: payload.datasource,
    query: payload.query,
    columns: 'columns' in payload ? payload.columns : undefined,
    workspaceIds: 'workspaceIds' in payload ? payload.workspaceIds : undefined,
  };
};

const setBuilderSeed = (
  state: ReportNavigationState,
  payload: NavigateToReportBuilderPayload
): ReportNavigationState => ({ ...state, ...toBuilderSeed(payload) });

const setReaderContext = (
  state: ReportNavigationState,
  {
    reportId,
    dashboardId,
    dashboardFilterValues,
  }: NavigateToReportModulePayload
): ReportNavigationState => ({
  ...state,
  dashboardId,
  dashboardFilterValues,
  readerContextReportId: reportId,
});

export const reportNavigation$ = reducedStream<ReportNavigationState>(
  'reportNavigation$',
  defaultState,
  [
    streamReducer(reportRouteNavigation$, applyRouteFields),
    reducer(reportNavigationReset, resetState(defaultState)),
    reducer(navigateToReportBuilder, setBuilderSeed),
    reducer(navigateToReportReader, setReaderContext),
  ]
);
